import React from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle } from 'lucide-react'
import { GraphQLError } from 'graphql'
import { Card, CardContent } from '../ui/card'
import { Button } from '../ui/button'
import {
  CATEGORY_ACTION,
  CATEGORY_MESSAGE_KEY,
  categorize,
} from './errorCategory'

interface InlineErrorProps {
  error?: Error | GraphQLError | null
  onRetry?: () => void
  fullHeight?: boolean
  /**
   * 상위 컴포넌트가 이미 `errorReporter.capture()`를 호출하고 받은 id를 내려줄 때 사용.
   * 전달되지 않으면 표시만 생략한다 — InlineError는 **중복 캡처를 피하기 위해**
   * 자체적으로 capture를 호출하지 않는다. 실제 Apollo 에러는 `graphql/client.ts`의
   * `errorLink`가, React 렌더 크래시는 `ErrorBoundaryWrapper`가 이미 캡처한다.
   */
  errorId?: string
}

/**
 * Level 2 — 페이지 내부 데이터 로딩 실패 시 표시되는 인라인 에러 카드.
 * 기존 ErrorBoundary 인라인 사용법(`<ErrorBoundary error={} onRetry={} />`)과
 * 완전 호환되며, 카테고리 기반 i18n 메시지를 사용해 기술 메시지를 감춘다.
 */
export const InlineError: React.FC<InlineErrorProps> = ({
  error,
  onRetry,
  fullHeight = true,
  errorId,
}) => {
  const { t } = useTranslation('common')

  if (!error) return null

  const category = categorize(error)
  const messageKey = CATEGORY_MESSAGE_KEY[category]
  const action = CATEGORY_ACTION[category]

  return (
    <Card className={fullHeight ? 'm-4' : ''}>
      <CardContent
        className={`flex flex-col items-center justify-center space-y-3 text-center ${
          fullHeight ? 'h-96' : 'py-8'
        }`}
      >
        <AlertCircle className="w-12 h-12 text-destructive" />
        <p className="text-destructive text-lg font-semibold">
          {t('error', '오류')}
        </p>
        <p className="text-muted-foreground max-w-md">
          {t(messageKey, t('unknownError', '알 수 없는 오류가 발생했습니다.'))}
        </p>

        {errorId && (
          <p className="text-xs text-muted-foreground">
            {t('errorPage.errorIdLabel', '에러 ID')}:{' '}
            <span className="font-mono">{errorId}</span>
          </p>
        )}

        {action === 'retry' && onRetry && (
          <Button onClick={onRetry} variant="outline">
            {t('retry', '다시 시도')}
          </Button>
        )}
        {action === 'home' && (
          <Button
            onClick={() => (window.location.href = '/')}
            variant="outline"
          >
            {t('errorPage.goHome', '홈으로')}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}
